import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useTelegram } from '@/hooks/useTelegram';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { AudioProvider } from '@/contexts/AudioContext';

// Pages
import { HomePage } from '@/pages/Home/HomePage';
import { ModeSelectPage } from '@/pages/ModeSelect/ModeSelectPage';
import { CharacterCreationPage } from '@/pages/CharacterCreation/CharacterCreationPage';
import { CharacterSelectPage } from '@/pages/CharacterSelect/CharacterSelectPage';
import { AdventurePage } from '@/pages/Adventure/AdventurePage';
import { AdventureStartPage } from '@/pages/Adventure/AdventureStartPage';
import { SessionsListPage } from '@/pages/Adventure/SessionsListPage';
import { InventoryPage } from '@/pages/Inventory/InventoryPage';
import { GMToolsPage } from '@/pages/GMTools/GMToolsPage';
import { PrinciplesPage } from '@/pages/GMTools/PrinciplesPage';
import { GMMovesPage } from '@/pages/GMTools/GMMovesPage';
import { MonstersPage } from '@/pages/GMTools/MonstersPage';
import { TreasuresPage } from '@/pages/GMTools/TreasuresPage';
import { FrontsPage } from '@/pages/GMTools/FrontsPage';
import { WorldBuildingPage } from '@/pages/GMTools/WorldBuildingPage';
import { FirstSessionPage } from '@/pages/GMTools/FirstSessionPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

function AppRoutes() {
  const { isLoading, isAuthenticated, character } = useAuth();
  const { isReady } = useTelegram();

  // Show loading while not ready
  if (!isReady || isLoading) {
    return <LoadingScreen />;
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  // Get game mode from localStorage
  const gameMode = localStorage.getItem('gameMode');

  // Determine default route based on mode and character
  const getDefaultRoute = () => {
    if (!gameMode) return '/mode-select';
    if (gameMode === 'gm') return '/gm-tools';
    if (gameMode === 'solo') {
      return character?.characterClass ? '/' : '/character-creation';
    }
    return '/mode-select';
  };

  return (
    <Routes>
      {/* Main routes */}
      <Route path="/" element={
        gameMode === 'solo' && character?.characterClass
          ? <HomePage />
          : <Navigate to={getDefaultRoute()} replace />
      } />

      {/* Mode selection */}
      <Route path="/mode-select" element={<ModeSelectPage />} />

      {/* Character management */}
      <Route path="/character-select" element={<CharacterSelectPage />} />
      <Route path="/character-creation" element={<CharacterCreationPage />} />

      {/* Adventure (Solo mode) */}
      <Route path="/adventure" element={<AdventureStartPage />} />
      <Route path="/adventure/play" element={<AdventurePage />} />
      <Route path="/adventure/sessions" element={<SessionsListPage />} />
      <Route path="/inventory" element={<InventoryPage />} />

      {/* GM Tools */}
      <Route path="/gm-tools" element={<GMToolsPage />} />
      <Route path="/gm/principles" element={<PrinciplesPage />} />
      <Route path="/gm/moves" element={<GMMovesPage />} />
      <Route path="/gm/monsters" element={<MonstersPage />} />
      <Route path="/gm/treasures" element={<TreasuresPage />} />
      <Route path="/gm/fronts" element={<FrontsPage />} />
      <Route path="/gm/world-building" element={<WorldBuildingPage />} />
      <Route path="/gm/first-session" element={<FirstSessionPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AudioProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AudioProvider>
    </QueryClientProvider>
  );
}
